<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { computed } from "vue"
import { BaseModal, Button, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { ConfirmModalKey } from "@/symbols"

interface ConfirmModalProps {
  name?: string | symbol
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  variant?: "primary" | "danger"
  icon?: IconKeys
  isLoading?: boolean
}

const props = withDefaults(defineProps<ConfirmModalProps>(), {
  name: () => ConfirmModalKey,
  variant: "primary",
})

const emit = defineEmits<{
  (e: "confirm"): void
  (e: "cancel"): void
}>()

const { t } = useTranslation()

const modalConfig = computed(() => ({
  title: props.title ?? t("confirm_modal.title"),
  message: props.message ?? t("confirm_modal.message"),
  confirmText: props.confirmText ?? t("confirm_modal.confirm_text"),
  cancelText: props.cancelText ?? t("confirm_modal.cancel_text"),
}))

const { toggle } = useModal(props.name)

function handleConfirm() {
  emit("confirm")
}

function handleCancel() {
  emit("cancel")
  toggle()
}
</script>

<template>
  <BaseModal :name="name" data-test="confirm-modal">
    <div class="relative mx-auto flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-lg">
      <Button
        variant="flat"
        size="xs"
        class="absolute right-3 top-3 z-10 size-8 p-0"
        :disabled="isLoading"
        @click="handleCancel"
      >
        <Icon icon="Close" size="sm" />
        <span class="sr-only">{{ t("close") }}</span>
      </Button>

      <div class="flex items-start gap-4 p-6 pb-4 pe-12">
        <div
          v-if="icon || variant === 'danger'"
          class="flex size-11 shrink-0 items-center justify-center rounded-full"
          :class="variant === 'danger' ? 'bg-theme-danger/10 text-theme-danger' : 'bg-primary/10 text-primary'"
          aria-hidden="true"
        >
          <Icon :icon="icon ?? 'Caution'" size="sm" />
        </div>

        <div class="min-w-0 flex-1 pt-0.5">
          <h1 class="font-heading text-lg font-semibold leading-snug text-primary md:text-xl">
            {{ modalConfig.title }}
          </h1>

          <div class="mt-2 font-body text-sm leading-relaxed md:text-base">
            <slot name="message">
              <p>
                {{ modalConfig.message }}
              </p>
            </slot>
          </div>
        </div>
      </div>

      <div class="border-t border-primary/10 px-6 py-4">
        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            variant="tertiary"
            class="w-full sm:w-auto"
            :disabled="isLoading"
            data-test="cancel-button"
            @click="handleCancel"
          >
            {{ modalConfig.cancelText }}
          </Button>
          <Button
            :variant="variant"
            class="w-full sm:w-auto"
            :disabled="isLoading"
            data-test="confirm-button"
            @click="handleConfirm"
          >
            <template v-if="isLoading">
              {{ modalConfig.confirmText }} <Icon icon="Loading" data-test="loading-icon" />
            </template>
            <template v-else>
              {{ modalConfig.confirmText }}
            </template>
          </Button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
